import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomBreadcrumb, CustomInput, CustomButton, CustomModal, CustomSelect, CustomSearchableSelect } from '@/shared/components/atoms';
import { CustomCommonScreenHeader, CustomTable, CustomTableColumn, ConfirmationModal, ImportPreviewModal } from '@/shared/components/molecules';
import { Home, GraduationCap, Pencil, Trash2, Download, Upload, Search, X } from 'lucide-react';
import { parentApi } from '../api/parentApi';
import { classApi } from '../api/classApi';
import { Student } from '../types';
import { useToastStore } from '@/shared/stores/toastStore';
import { useAuthStore } from '@/shared/stores/authStore';

export const ListStudents = () => {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const { showToast } = useToastStore();
    const todayStr = new Date().toLocaleDateString('en-CA');

    const [students, setStudents] = useState<Student[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [hasNext, setHasNext] = useState(false);
    const [hasPrevious, setHasPrevious] = useState(false);

    // Search and Sort states
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    const [isDownloading, setIsDownloading] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [importFile, setImportFile] = useState<File | null>(null);
    const [isImportPreviewOpen, setIsImportPreviewOpen] = useState(false);
    const importFileInputRef = useRef<HTMLInputElement>(null);

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 350);
        return () => clearTimeout(timer);
    }, [search]);

    // Parents list for dropdown select
    const [parentsOptions, setParentsOptions] = useState<{ label: string; value: string }[]>([]);

    // Classes list for dropdown select
    const [classesOptions, setClassesOptions] = useState<{ label: string; value: string }[]>([]);

    // Add student modal state
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [addName, setAddName] = useState('');
    const [addClass, setAddClass] = useState('');
    const [addSection, setAddSection] = useState('');
    const [addAdmissionNum, setAddAdmissionNum] = useState('');
    const [addDob, setAddDob] = useState('');
    const [addRollNum, setAddRollNum] = useState('');
    const [addGender, setAddGender] = useState('');
    const [addSession, setAddSession] = useState('');
    const [isAdding, setIsAdding] = useState(false);

    // Edit student modal state
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
    const [editName, setEditName] = useState('');
    const [editClass, setEditClass] = useState('');
    const [editSection, setEditSection] = useState('');
    const [editAdmissionNum, setEditAdmissionNum] = useState('');
    const [editDob, setEditDob] = useState('');
    const [editRollNum, setEditRollNum] = useState('');
    const [editGender, setEditGender] = useState('');
    const [editSession, setEditSession] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);

    // Delete student state
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [studentToDelete, setStudentToDelete] = useState<Student | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Validation errors state
    const [addErrors, setAddErrors] = useState<Record<string, string>>({});
    const [editErrors, setEditErrors] = useState<Record<string, string>>({});

    const schoolSlug = user?.school?.slug || '';

    const handleDownloadTemplate = async () => {
        if (!schoolSlug) return;
        setIsDownloading(true);
        try {
            const response = await parentApi.downloadTemplate(schoolSlug, 'students');
            if (response.success && response.data) {
                const contentDisposition = response.headers?.['content-disposition'];
                let fileName = 'students_template.xlsx';
                if (contentDisposition) {
                    const match = contentDisposition.match(/filename=(.+)/);
                    if (match && match[1]) {
                        fileName = match[1].replace(/['"]/g, '');
                    }
                }
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                showToast('Template downloaded successfully', 'success');
            } else {
                showToast(response.error || 'Failed to download template', 'error');
            }
        } catch (error) {
            showToast('Failed to download template', 'error');
        } finally {
            setIsDownloading(false);
        }
    };

    const handleFileImport = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !schoolSlug) return;

        // Validate file extension
        const allowedExtensions = ['xlsx', 'xls', 'csv'];
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        
        if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
            showToast('Please upload only Excel (.xlsx, .xls) or CSV (.csv) files.', 'error');
            if (importFileInputRef.current) {
                importFileInputRef.current.value = '';
            }
            return;
        }

        setImportFile(file);
        setIsImportPreviewOpen(true);
        if (importFileInputRef.current) {
            importFileInputRef.current.value = '';
        }
    };

    const fetchStudents = useCallback(async () => {
        if (!schoolSlug) return;
        setIsLoading(true);
        try {
            const response = await parentApi.getStudents(schoolSlug, {
                page,
                page_size: pageSize,
                search: debouncedSearch.trim() || undefined,
                sort_by: sortBy,
                sort_order: sortOrder
            });
            if (response.success && response.data) {
                const data = response.data;
                setStudents(data.items || []);
                setTotalCount(data.total_count || 0);
                setTotalPages(data.total_pages || 1);
                setHasNext(data.has_next || false);
                setHasPrevious(data.has_previous || false);
            } else {
                showToast(response.error || 'Failed to load students', 'error');
            }
        } catch (error) {
            showToast('Failed to load students', 'error');
        } finally {
            setIsLoading(false);
        }
    }, [schoolSlug, page, pageSize, debouncedSearch, sortBy, sortOrder, showToast]);

    // Fetch parents list once for the select dropdown
    const fetchParentsList = useCallback(async () => {
        if (!schoolSlug) return;
        try {
            const response = await parentApi.getParents(schoolSlug, { page: 1, page_size: 100 });
            if (response.success && response.data) {
                const items = response.data.items || [];
                setParentsOptions(
                    items.map((p) => ({
                        label: `${p.name || ''} (${p.email || 'No email'})`,
                        value: p.user_id
                    }))
                );
            }
        } catch (error) {
            console.error('Failed to fetch parents for dropdown', error);
        }
    }, [schoolSlug]);

    // Fetch classes list once for the select dropdown
    const fetchClassesList = useCallback(async () => {
        if (!schoolSlug) return;
        try {
            const response = await classApi.getClasses(schoolSlug, { page: 1, page_size: 50 });
            if (response.success && response.data) {
                const data = response.data as any;
                const items = Array.isArray(data) ? data : (data.items || []);
                setClassesOptions(
                    items.map((c: any) => ({
                        label: c.name,
                        value: c.name
                    }))
                );
            }
        } catch (error) {
            console.error('Failed to fetch classes for dropdown', error);
        }
    }, [schoolSlug]);

    useEffect(() => {
        fetchStudents();
    }, [fetchStudents]);

    useEffect(() => {
        fetchParentsList();
    }, [fetchParentsList]);

    useEffect(() => {
        fetchClassesList();
    }, [fetchClassesList]);

    const handleAddClick = () => {
        setAddName('');
        setAddClass('');
        setAddSection('');
        setAddAdmissionNum('');
        setAddDob('');
        setAddRollNum('');
        setAddGender('');
        setAddSession('');
        setAddErrors({});
        setIsAddModalOpen(true);
    };

    const handleCreateStudent = async () => {
        if (!schoolSlug) return;

        const errors: Record<string, string> = {};
        if (!addName.trim()) errors.name = 'Student name is required';
        if (!addGender) errors.gender = 'Gender is required';
        if (!addSession.trim()) errors.session = 'Session is required';
        if (!addClass) errors.class = 'Class is required';
        if (!addSection.trim()) errors.section = 'Section is required';
        if (!addRollNum.trim()) {
            errors.rollNum = 'Roll number is required';
        } else if (!/^\d+$/.test(addRollNum.trim())) {
            errors.rollNum = 'Roll number must be a valid number';
        }
        if (!addAdmissionNum.trim()) errors.admissionNum = 'Admission number is required';
        if (!addDob) errors.dob = 'Date of birth is required';

        if (Object.keys(errors).length > 0) {
            setAddErrors(errors);
            return;
        }
        setAddErrors({});

        setIsAdding(true);
        try {
            const response = await parentApi.addStudent(schoolSlug, {
                name: addName,
                class: addClass,
                class_name: addClass,
                section: addSection,
                admission_number: addAdmissionNum,
                dob: addDob || undefined,
                roll_number: addRollNum || undefined,
                gender: addGender || undefined,
                session: addSession || undefined
            });

            if (response.success) {
                showToast('Student created successfully', 'success');
                setIsAddModalOpen(false);
                fetchStudents();
            } else {
                showToast(response.error || 'Failed to add student', 'error');
            }
        } catch (error) {
            showToast('Failed to add student', 'error');
        } finally {
            setIsAdding(false);
        }
    };

    const handleEditClick = (student: Student) => {
        setSelectedStudent(student);
        setEditName(student.name || '');
        setEditClass(student.class || student.class_name || '');
        setEditSection(student.section || '');
        setEditAdmissionNum(student.admission_number || '');
        setEditDob(student.dob ? student.dob.split('T')[0] : '');
        setEditRollNum(student.roll_number || '');
        setEditGender(student.gender || '');
        setEditSession(student.session || '');
        setEditErrors({});
        setIsEditModalOpen(true);
    };

    const handleUpdateStudent = async () => {
        if (!schoolSlug || !selectedStudent) return;

        const errors: Record<string, string> = {};
        if (!editName.trim()) errors.name = 'Student name is required';
        if (!editGender) errors.gender = 'Gender is required';
        if (!editSession.trim()) errors.session = 'Session is required';
        if (!editClass) errors.class = 'Class is required';
        if (!editSection.trim()) errors.section = 'Section is required';
        if (!editRollNum.trim()) {
            errors.rollNum = 'Roll number is required';
        } else if (!/^\d+$/.test(editRollNum.trim())) {
            errors.rollNum = 'Roll number must be a valid number';
        }
        if (!editAdmissionNum.trim()) errors.admissionNum = 'Admission number is required';
        if (!editDob) errors.dob = 'Date of birth is required';

        if (Object.keys(errors).length > 0) {
            setEditErrors(errors);
            return;
        }
        setEditErrors({});

        setIsUpdating(true);
        try {
            const response = await parentApi.updateStudent(schoolSlug, selectedStudent.id, {
                name: editName,
                class: editClass,
                class_name: editClass,
                section: editSection,
                admission_number: editAdmissionNum,
                dob: editDob || undefined,
                roll_number: editRollNum || undefined,
                gender: editGender || undefined,
                session: editSession || undefined
            });

            if (response.success) {
                showToast('Student updated successfully', 'success');
                setIsEditModalOpen(false);
                fetchStudents();
            } else {
                showToast(response.error || 'Failed to update student', 'error');
            }
        } catch (error) {
            showToast('Failed to update student', 'error');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDeleteClick = (student: Student) => {
        setStudentToDelete(student);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!schoolSlug || !studentToDelete) return;

        setIsDeleting(true);
        try {
            const response = await parentApi.deleteStudent(schoolSlug, studentToDelete.id);
            if (response.success) {
                showToast('Student record deleted successfully', 'success');
                setIsDeleteModalOpen(false);
                fetchStudents();
            } else {
                showToast(response.error || 'Failed to delete student', 'error');
            }
        } catch (error) {
            showToast('Failed to delete student', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    const columns: CustomTableColumn<Student>[] = [
        {
            header: 'Student Name',
            key: 'name',
            sortable: true,
            minWidth: '250px',
            render: (student) => {
                const isMale = student.gender?.toUpperCase() === 'M' || student.gender?.toUpperCase() === 'MALE';
                const isFemale = student.gender?.toUpperCase() === 'F' || student.gender?.toUpperCase() === 'FEMALE';
                const genderLabel = isMale ? 'Male' : isFemale ? 'Female' : student.gender;

                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
                        <div
                            style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                background: 'var(--accent-primary-light, rgba(99, 102, 241, 0.1))',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'var(--accent-primary, #4f46e5)',
                                fontWeight: '600',
                                flexShrink: 0
                            }}
                        >
                            {student.name ? student.name.charAt(0).toUpperCase() : 'S'}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', minWidth: 0 }}>
                                <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                                    {student.name}
                                </span>
                                {student.gender && (
                                    <span style={{
                                        fontSize: '0.68rem',
                                        background: isMale ? 'rgba(59, 130, 246, 0.1)' : isFemale ? 'rgba(236, 72, 153, 0.1)' : 'var(--bg-secondary)',
                                        border: `1px solid ${isMale ? 'rgba(59, 130, 246, 0.2)' : isFemale ? 'rgba(236, 72, 153, 0.2)' : 'var(--border-color)'}`,
                                        color: isMale ? '#2563eb' : isFemale ? '#db2777' : 'var(--text-secondary)',
                                        padding: '1px 6px',
                                        borderRadius: '6px',
                                        fontWeight: '600',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {genderLabel}
                                    </span>
                                )}
                            </div>
                            {student.parents && student.parents.length > 0 ? (
                                <span style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '2px', wordBreak: 'break-word' }}>
                                    <strong style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Parent:</strong> {student.parents.map((p) => p.name || p.email).join(', ')}
                                </span>
                            ) : student.created_at ? (
                                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                                    Added on {new Date(student.created_at).toLocaleDateString()}
                                </span>
                            ) : null}
                        </div>
                    </div>
                );
            }
        },
        {
            header: 'Class / Section',
            key: 'class' as any,
            sortKey: 'class',
            sortable: true,
            minWidth: '120px',
            render: (student) => {
                const className = student.class || student.class_name || 'N/A';
                const section = student.section || '';
                return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: '500' }}>
                            {className}
                            {section && ` - ${section}`}
                        </span>
                        {student.session && (
                            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                                Session: {student.session}
                            </span>
                        )}
                    </div>
                );
            }
        },
        {
            header: 'Roll Number',
            key: 'roll_number' as any,
            sortable: true,
            minWidth: '100px',
            render: (student) => (
                <span style={{ fontWeight: '500' }}>
                    {student.roll_number || 'N/A'}
                </span>
            )
        },
        {
            header: 'Admission No.',
            key: 'admission_number' as any,
            sortable: true,
            minWidth: '120px',
            render: (student) => (
                <span style={{ fontFamily: 'monospace', fontWeight: '500' }}>
                    {student.admission_number || 'N/A'}
                </span>
            )
        },
        {
            header: 'Date of Birth',
            key: 'dob' as any,
            sortable: true,
            minWidth: '110px',
            render: (student) => {
                let dobStr = 'N/A';
                if (student.dob) {
                    const dobClean = student.dob.split('T')[0];
                    const date = new Date(dobClean.replace(/-/g, '/'));
                    if (!isNaN(date.getTime())) {
                        dobStr = date.toLocaleDateString();
                    } else {
                        dobStr = dobClean;
                    }
                }
                return (
                    <span style={{ color: 'var(--text-secondary)' }}>
                        {dobStr}
                    </span>
                );
            }
        },
        {
            header: 'Actions',
            key: 'actions',
            align: 'right',
            minWidth: '100px',
            width: '100px',
            render: (student) => (
                <div className="table-action-cell" style={{ justifyContent: 'flex-end' }}>
                    <button
                        className="table-action-btn"
                        onClick={(e) => { e.stopPropagation(); handleEditClick(student); }}
                        title="Edit Student"
                    >
                        <Pencil size={18} />
                    </button>
                    <button
                        className="table-action-btn danger"
                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(student); }}
                        title="Delete Student"
                    >
                        <Trash2 size={18} />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="page-container">
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Students', icon: <GraduationCap size={14} /> }
                ]}
            />
            <CustomCommonScreenHeader
                title="Student Management"
                subtitle="View and manage students enrolled in your school."
                icon={<GraduationCap size={24} />}
                actionLabel="Add Student"
                onActionClick={handleAddClick}
            />

            {/* Responsive Action & Filter Bar (Search, Import, Download) */}
            <div className="table-filter-bar">
                {/* Search Box */}
                <div className="table-search-box">
                    <Search size={16} className="table-search-icon" />
                    <input
                        type="text"
                        placeholder="Search by student name, admission no, class..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && (
                        <button
                            className="table-search-clear"
                            onClick={() => setSearch('')}
                            title="Clear search"
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>

                {/* Import / Download Actions */}
                <div className="table-filter-actions">
                    <CustomButton
                        variant="outline"
                        size="small"
                        onClick={handleDownloadTemplate}
                        disabled={isDownloading}
                    >
                        <Download size={16} style={{ marginRight: '0.5rem' }} />
                        {isDownloading ? 'Downloading...' : 'Download Template'}
                    </CustomButton>
                    
                    <CustomButton
                        variant="outline"
                        size="small"
                        onClick={() => importFileInputRef.current?.click()}
                        disabled={isImporting}
                    >
                        <Upload size={16} style={{ marginRight: '0.5rem' }} />
                        {isImporting ? 'Importing...' : 'Import Students'}
                    </CustomButton>
                    <input
                        type="file"
                        ref={importFileInputRef}
                        onChange={handleFileImport}
                        accept=".xlsx, .xls, .csv"
                        style={{ display: 'none' }}
                    />
                </div>
            </div>

            <CustomTable
                columns={columns}
                data={students}
                isLoading={isLoading}
                isCompact={true}
                minWidth="800px"
                emptyMessage={debouncedSearch ? `No students matching "${debouncedSearch}"` : 'No students found'}
                emptyIcon={<GraduationCap size={32} />}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={(colKey, order) => {
                    setSortBy(colKey);
                    setSortOrder(order);
                    setPage(1);
                }}
                pagination={{
                    currentPage: page,
                    totalPages: totalPages,
                    totalCount: totalCount,
                    pageSize: pageSize,
                    onPageChange: setPage,
                    hasPrevious: hasPrevious,
                    hasNext: hasNext,
                    isLoading: isLoading,
                    itemName: 'students',
                }}
            />

            {/* Add Student Modal */}
            <CustomModal
                isOpen={isAddModalOpen}
                onClose={() => !isAdding && setIsAddModalOpen(false)}
                title="Add New Student"
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', width: '100%' }}>
                        <CustomButton
                            variant="outline"
                            color="secondary"
                            onClick={() => setIsAddModalOpen(false)}
                            disabled={isAdding}
                        >
                            Cancel
                        </CustomButton>
                        <CustomButton
                            variant="filled"
                            color="primary"
                            onClick={handleCreateStudent}
                            loading={isAdding}
                        >
                            Add Student
                        </CustomButton>
                    </div>
                }
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', padding: '1rem 0' }}>
                    <CustomInput
                        name="addStudentName"
                        label="Student Name"
                        placeholder="Enter student's full name"
                        value={addName}
                        onChange={(e) => setAddName(e.target.value)}
                        required
                        error={addErrors.name}
                    />
                    {/* Parent selection removed as per updated API */}
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <div style={{ flex: 1 }}>
                            <CustomSelect
                                name="addStudentGender"
                                label="Gender"
                                placeholder="Select Gender"
                                options={[
                                    { label: 'Male', value: 'M' },
                                    { label: 'Female', value: 'F' }
                                ]}
                                value={addGender}
                                onChange={(e) => setAddGender(e.target.value)}
                                required
                                error={addErrors.gender}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <CustomInput
                                name="addStudentSession"
                                label="Session"
                                placeholder="e.g. 2026-2027"
                                value={addSession}
                                onChange={(e) => setAddSession(e.target.value)}
                                required
                                error={addErrors.session}
                            />
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <div style={{ flex: 1 }}>
                            <CustomSearchableSelect
                                label="Class"
                                placeholder="Select Class"
                                options={classesOptions}
                                value={addClass}
                                onChange={(val) => setAddClass(val as string)}
                                leftIcon={<GraduationCap size={18} />}
                                hideAvatar
                                required
                                error={addErrors.class}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <CustomInput
                                name="addStudentSection"
                                label="Section"
                                placeholder="e.g. A"
                                value={addSection}
                                onChange={(e) => setAddSection(e.target.value)}
                                required
                                error={addErrors.section}
                            />
                        </div>
                    </div>
                   
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <div style={{ flex: 1 }}>
                            <CustomInput
                                name="addStudentRollNum"
                                label="Roll Number"
                                type="number"
                                placeholder="Enter roll number"
                                value={addRollNum}
                                onChange={(e) => setAddRollNum(e.target.value.replace(/\D/g, ''))}
                                required
                                error={addErrors.rollNum}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <CustomInput
                                name="addStudentAdmissionNum"
                                label="Admission Number"
                                placeholder="Enter admission number"
                                value={addAdmissionNum}
                                onChange={(e) => setAddAdmissionNum(e.target.value)}
                                required
                                error={addErrors.admissionNum}
                            />
                        </div>
                    </div>
                    <CustomInput
                        name="addStudentDob"
                        label="Date of Birth"
                        type="date"
                        placeholder="YYYY-MM-DD"
                        value={addDob}
                        onChange={(e) => setAddDob(e.target.value)}
                        required
                        error={addErrors.dob}
                        max={todayStr}
                    />
                </div>
            </CustomModal>

            {/* Edit Student Modal */}
            <CustomModal
                isOpen={isEditModalOpen}
                onClose={() => !isUpdating && setIsEditModalOpen(false)}
                title="Edit Student Details"
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', width: '100%' }}>
                        <CustomButton
                            variant="outline"
                            color="secondary"
                            onClick={() => setIsEditModalOpen(false)}
                            disabled={isUpdating}
                        >
                            Cancel
                        </CustomButton>
                        <CustomButton
                            variant="filled"
                            color="primary"
                            onClick={handleUpdateStudent}
                            loading={isUpdating}
                        >
                            Save Changes
                        </CustomButton>
                    </div>
                }
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', padding: '1rem 0' }}>
                    <CustomInput
                        name="studentName"
                        label="Student Name"
                        placeholder="Enter student's full name"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        required
                        error={editErrors.name}
                    />
                    {/* Parent selection removed as per updated API */}
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <div style={{ flex: 1 }}>
                            <CustomSelect
                                name="studentGender"
                                label="Gender"
                                placeholder="Select Gender"
                                options={[
                                    { label: 'Male', value: 'M' },
                                    { label: 'Female', value: 'F' }
                                ]}
                                value={editGender}
                                onChange={(e) => setEditGender(e.target.value)}
                                required
                                error={editErrors.gender}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <CustomInput
                                name="studentSession"
                                label="Session"
                                placeholder="e.g. 2026-2027"
                                value={editSession}
                                onChange={(e) => setEditSession(e.target.value)}
                                required
                                error={editErrors.session}
                            />
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <div style={{ flex: 1 }}>
                            <CustomSearchableSelect
                                label="Class"
                                placeholder="Select Class"
                                options={classesOptions}
                                value={editClass}
                                onChange={(val) => setEditClass(val as string)}
                                leftIcon={<GraduationCap size={18} />}
                                hideAvatar
                                required
                                error={editErrors.class}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <CustomInput
                                name="studentSection"
                                label="Section"
                                placeholder="e.g. A"
                                value={editSection}
                                onChange={(e) => setEditSection(e.target.value)}
                                required
                                error={editErrors.section}
                            />
                        </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <div style={{ flex: 1 }}>
                            <CustomInput
                                name="studentRollNum"
                                label="Roll Number"
                                type="number"
                                placeholder="Enter roll number"
                                value={editRollNum}
                                onChange={(e) => setEditRollNum(e.target.value.replace(/\D/g, ''))}
                                required
                                error={editErrors.rollNum}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <CustomInput
                                name="studentAdmissionNum"
                                label="Admission Number"
                                placeholder="Enter admission number"
                                value={editAdmissionNum}
                                onChange={(e) => setEditAdmissionNum(e.target.value)}
                                required
                                error={editErrors.admissionNum}
                            />
                        </div>
                    </div>
                    <CustomInput
                        name="studentDob"
                        label="Date of Birth"
                        type="date"
                        placeholder="YYYY-MM-DD"
                        value={editDob}
                        onChange={(e) => setEditDob(e.target.value)}
                        required
                        error={editErrors.dob}
                        max={todayStr}
                    />
                </div>
            </CustomModal>

            {/* Delete Student Modal */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Student Record"
                message={`Are you sure you want to delete the record for ${studentToDelete?.name}? This action cannot be undone.`}
                isLoading={isDeleting}
                confirmLabel="Delete"
                variant="danger"
            />

            <ImportPreviewModal
                isOpen={isImportPreviewOpen}
                onClose={() => {
                    setIsImportPreviewOpen(false);
                    setImportFile(null);
                }}
                file={importFile}
                type="students"
                onImport={(file) => parentApi.importFile(schoolSlug, 'students', file)}
                onSuccess={() => {
                    fetchStudents();
                }}
            />
        </div>
    );
};
